
import dayjs from 'dayjs';
import { cloneDeep } from "@/utils";
import { getLineChartDefaultColors } from '@/utils/colorHelper/index.js';

const $str = window.$str;


const { defaultColorList, textColor, axisColor } = getLineChartDefaultColors();

/**
 * 获取linesChart的配置对象
 * @param {*} chartData 图表要渲染的数据
 * @param {*} chartData.startTime X轴的最小坐标值
 * @param {*} chartData.endTime X轴的最大坐标值
 * @returns 
 */
export function getChartOption(chartData = { lines: [] }) {
    const startTime = chartData.startTime ?? dayjs().startOf('day').format('YYYY-MM-DD 00:00:00');
    const endTime = chartData.endTime ?? dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss');
    const { yMin, yMax } = getYaxisRange(chartData);
    const series = getSeries(...arguments);
    const colorList = getSeriesColorList(series);

    return {
        /** 配置调色板 */
        color: colorList,
        /** 坐标系-X轴 */
        xAxis: [
            {
                gridIndex: 0, // 坐标系idx
                // name: '时间', // 轴线名称
                type: 'time', // 时间轴
                nameLocation: 'end',
                min: startTime, // 最小坐标值
                max: endTime, // 最大坐标值
                splitNumber: 12,
                boundaryGap: false, // 坐标轴两边留白策略。时间轴，该值是一个两个值的数组，表示数据最小值和最大值的延伸范围，数值或%，在设置 min 和 max 后无效。
                axisLine: { // 坐标轴轴线的配置
                    lineStyle: {
                        color: axisColor
                    }
                },
                position: 'bottom', // 轴线展示位置
                nameTextStyle: {
                    color: textColor,
                },
                axisLabel: {
                    color: textColor, // 坐标文字的颜色
                    formatter: function (value, index) { // 格式化文本格式
                        if (dayjs(startTime).isSame(dayjs(endTime), 'day')) {
                            return dayjs(value).format('HH:mm');
                        }
                        return dayjs(value).format('MM-DD\nHH:mm');
                    },
                },
            },
        ],
        /** 坐标系-Y轴 */
        yAxis: [
            {
                gridIndex: 0, // 坐标系idx
                name: $str.getKey('数据分析/功率(kW)'),
                type: 'value',
                min: yMin,
                max: yMax,
                // nameGap: 15, // 坐标轴名称与轴线之间的距离。
                position: 'left',
                nameLocation: 'end',
                nameTextStyle: {
                    align: 'center',
                    color: textColor,
                    padding: [0, 0, 0, 35]
                },
                splitLine: {
                    lineStyle: {
                        color: '#eee',
                        type: 'dashed'
                    }
                },
                axisLabel: {
                    color: textColor,
                },
                axisLine: {
                    lineStyle: {
                        color: axisColor
                    }
                }
            },
            {
                gridIndex: 0, // 坐标系idx
                name: $str.getKey('数据分析/功率因数'),
                type: 'value',
                position: 'right',
                nameLocation: 'end',
                nameTextStyle: {
                    color: textColor
                },
                splitLine: {
                    lineStyle: {
                        color: '#eee',
                        type: 'dashed'
                    }
                },
                axisLabel: {
                    color: textColor,
                },
                axisLine: {
                    lineStyle: {
                        color: axisColor
                    }
                }
            }
        ],
        /** 直角坐标系内绘图网格 */
        grid: [
            {
                top: 55,
                left: 50,
                right: 40,
                // height: '30%'
                bottom: 80,
            },
        ],
        /** 要渲染的charts系列配置 */
        series,
        /** 全局坐标轴指示器: https://echarts.apache.org/zh/option.html#axisPointer */
        axisPointer: {
            link: [ // 不同轴的 axisPointer 可以进行联动: https://echarts.apache.org/zh/option.html#axisPointer.link
                {
                    xAxisIndex: 'all'
                }
            ],
            label: {
                backgroundColor: '#777'
            }
        },
        /** 提示框组件的配置 */
        tooltip: createTooltip(),
        /** 图例 */
        legend: {
            icon: 'circle',
            left: 'center',
            bottom: 25,
            // right: 40,
            // itemWidth: 8,
            // width: '80%', // 超出宽度范围，这行显示图例
            textStyle: {
                color: textColor
            },
        },
        /** 区域缩放组件：https://echarts.apache.org/zh/option.html#dataZoom */
        dataZoom: [
            {
                height: 12,
                bottom: 8,
                // type: 'slider', // 滑动条型数据区域缩放组件
                textStyle: {
                    color: '#8392A5' // dataZoom 文字的颜色
                },
                // 两侧缩放手柄的 icon 形状，支持路径字符串
                handleIcon: 'path://M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z',
                dataBackground: {
                    areaStyle: {
                        color: '#8392A5'
                    },
                    lineStyle: {
                        opacity: 0.8,
                        color: '#8392A5'
                    }
                },
                brushSelect: true, // 开启刷选功能。在下图的 brush 区域可以按住鼠标左键后框选出选中部分。
                xAxisIndex: [0, 1]
            },
            {
                type: 'inside', // 内置型数据区域缩放组件。在图像上可使用鼠标缩放和拖动。
                xAxisIndex: [0, 1]
            }
        ],
        /** 禁止刷新图表时的动画效果 */
        animation: false,
    }
}

/**
 * 获取Y坐标的刻度范围
 * >>> chartData.lines 存在有效数据，让echarts自行计算Y坐标刻度范围
 * >>> 否则：设置一个默认的刻度范围。避免：无数据时Y坐标轴不显示
 * @param {*} chartData 
 * @returns 
 */
function getYaxisRange(chartData = {}) {
    const result = {
        yMin: 0,
        yMax: 1800,
    }
    if (Array.isArray(chartData.lines)) {
        const hasUsefullData = chartData.lines.some(line => {
            return Array.isArray(line.data) && line.data.length > 0;
        })
        if (hasUsefullData) {
            result.yMin = undefined;
            result.yMax = undefined;
        }
    }

    return result;
}

/**
 * 获取系列配置项
 * >>> 设置series中每条曲线的配置项
 * >>> 如果存在多个坐标系，通过 'xAxisIndex' 'yAxisIndex' 选项配置曲线所在坐标系
 * @param {*} chartData 
 * @returns 
 */
const getSeries = (chartData = {}) => {
    const series = [];
    if (Array.isArray(chartData.lines)) {
        // ------ 基础配置项 ------
        const seriesItem = {
            name: '',
            type: 'line',
            xAxisIndex: 0,
            yAxisIndex: 0,
            showSymbol: false, // 是否显示数据位置的symbol符号：否，只在 tooltip hover 的时候显示。
            lineStyle: {
                width: 1,
            },
            data: [], // 线数据
        }

        // ------ areaStyle 对应的配置项 ------
        const defaultAreaStyle = {
            opacity: 0.3,
            origin: 'auto',
            color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                    {
                        offset: 1, color: '#0F0' // 实际曲线最小值处的色值
                    },
                    {
                        offset: 0, color: '#F00' // 实际曲线最大值处的色值
                    }
                ],
                global: false // 缺省为 false
            }
        }

        // ------ markLine 对应的配置项 ------
        const defaultMarkLineStyle = {
            data: [
                {
                    name: $str.getKey('数据分析/最大值'),
                    type: 'max',
                    label: {
                        formatter: (params) => {
                            return `${params.name}: ${params.value}`;
                        }
                    }
                },
                {
                    name: $str.getKey('数据分析/最小值'),
                    type: 'min',
                    label: {
                        formatter: (params) => {
                            return `${params.name}: ${params.value}`;
                        }
                    }
                }
            ],
            label: {
                show: true,
                position: 'insideEndTop',
                color: 'inherit',
            }
        }


        // ------ 设置每条曲线的配置项-并将设置结果添加到series中 ------
        chartData.lines.forEach(line => {
            const item = cloneDeep(seriesItem);
            item.name = line.name;
            item.data = line.data;

            // 设置曲线的颜色
            if (Object.prototype.hasOwnProperty.call(line.options, 'lineColor')) {
                item.lineStyle.color = line.options.lineColor;
                item.lineStyle.width = 1.5;
            }

            // 设置曲线的area样式
            if (line.options?.areaStyle?.show) {
                const areaStyle = cloneDeep(defaultAreaStyle);
                if (line.options?.areaStyle?.minColor) {
                    const minItem = areaStyle.color.colorStops.find(it => it.offset === 1);
                    if (minItem) {
                        minItem.color = line.options.areaStyle.minColor;
                    }
                }
                if (line.options?.areaStyle?.maxColor) {
                    const maxItem = areaStyle.color.colorStops.find(it => it.offset === 0);
                    if (maxItem) {
                        maxItem.color = line.options.areaStyle.maxColor;
                    }
                }

                item.areaStyle = areaStyle;
            }

            // 设置markLine的样式
            if (line.options?.markLineStyle?.show) {
                const markLineStyle = cloneDeep(defaultMarkLineStyle);
                const markLines = cloneDeep(markLineStyle.data);
                markLineStyle.data = [];
                if (line.options?.markLineStyle?.showMin) {
                    const minLine = markLines.find(it => it.type === 'min');
                    if (minLine) {
                        minLine.label.formatter = params => `${line.name}-${params.name}: ${params.value}`;
                        markLineStyle.data.push(minLine);
                    }

                    const maxLine = markLines.find(it => it.type === 'max');
                    if (maxLine) {
                        maxLine.label.formatter = params => `${line.name}-${params.name}: ${params.value}`;
                        markLineStyle.data.push(maxLine);
                    }
                }

                item.markLine = markLineStyle;
            }

            // 设置当前曲线所在的坐标系
            if (line.options) {
                Object.prototype.hasOwnProperty.call(line.options, 'xAxisIndex') ? item.xAxisIndex = line.options.xAxisIndex : '';
                Object.prototype.hasOwnProperty.call(line.options, 'yAxisIndex') ? item.yAxisIndex = line.options.yAxisIndex : '';
            }

            series.push(item)
        })

    }

    return series;
}

/**
 * 获取调色板对应的颜色列表
 * >>> 基于series
 * >>> 包含了某些线条的自定义颜色
 * @param {*} series 
 * @returns 
 */
function getSeriesColorList(series) {
    const colorList = [];
    if (Array.isArray(series)) {
        series.forEach((line, idx) => {
            if (line?.lineStyle?.color) {
                colorList.push(line.lineStyle.color)
            } else {
                const len = defaultColorList.length;
                const defaultColor = defaultColorList[idx % len];
                colorList.push(defaultColor);
            }
        })
    }
    return colorList;
}

/**
 * 创建一个自定义的ToolTip配置项
 * @param {*} config 
 * @returns 
 */
function createTooltip(config = {}) {
    const timeFormatter = (value) => {
        // 格式化时间：如果是时间戳，转换为年月日时分秒；如果是时间字符串，直接使用
        let timeValue = value;
        if (typeof timeValue === 'number' || (typeof timeValue === 'string' && /^\d+$/.test(timeValue))) {
            // 判断是否是时间戳（毫秒或秒）
            const timestamp = Number(timeValue);
            const isMillisecond = timestamp > 946684800000; // 2000-01-01的时间戳（毫秒）
            timeValue = dayjs(isMillisecond ? timestamp : timestamp * 1000).format('YYYY-MM-DD HH:mm:ss');
        } else if (timeValue instanceof Date) {
            timeValue = dayjs(timeValue).format('YYYY-MM-DD HH:mm:ss');
        }
        return timeValue;
    };

    return {
        trigger: 'axis',
        axisPointer: {
            type: 'cross'
        },
        className: 'chart-tooltip',
        backgroundColor: 'rgba(255, 255, 255, 0.90)',
        borderWidth: 0,
        textStyle: {
            color: '#6A727C'
        },
        formatter(params) {
            let time = params[0].axisValue;
            time = timeFormatter(time);

            const str = (list) => {
                let t = '';
                for (const item of list) {
                    const originPrice = Array.isArray(item.data) ? item.data[1] : item.data;
                    const price = formatPriceValueForShow(originPrice);
                    t += /* html */`
            <div class="chart-tooltip__series" style="display: flex; align-items: center; justify-content: space-between; gap: 8px; line-height: 1.5;">
              <div style="display: flex; align-items: center; gap: 6px; flex: 1;">
                <div class="chart-tooltip__point" style="background: ${item.color}; width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; display: inline-block; vertical-align: middle;"></div>
                <div class="chart-tooltip__series-name" style="display: inline-block; vertical-align: middle;">${item.seriesName}</div>
              </div>
              <div class="chart-tooltip__value" style="flex-shrink: 0; display: inline-block; vertical-align: middle; text-align: right;">${price ?? '--'}</div>
            </div>`;
                }
                return t;
            };

            return /* html */`
        <div class="chart-tooltip__time" style="margin-bottom: 12px;">${time}</div>
        <div class="chart-tooltip__items-container">${str(params)}</div>
        `;
        }
    }
}


/** 格式化：价格展示时-只保留两位小数 */
function formatPriceValueForShow(price) {
    if (typeof (price) === 'number' || isNaN(price)) {
        return (+price).toFixed(2);
    } else {
        return price
    }
}

